import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';

import { useParams } from 'react-router-dom';
import {
  useCreateCourseMutation,
  useCreateLessonMutation,
  useGetSingleCourseQuery,
  useMigrateLessonMutation,
  useUpdateLessonMutation,
} from '../../../../reduxToolkit/reducers/usersSlice';
import { FormEvent, useEffect, useState } from 'react';
import Breadcrumbs from '../../../../coreComponents/breadcrumbs';
import { Primary } from '../../../../constants';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import AjaxSourcedData from '../../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { getLessonsListTableColumns } from '../../../../data/formsAndTable/tables/dataTables/DataSource';
import {
  CourseCategory,
  CourseData,
  CourseLesson,
  CourseLessonEditData,
  CourseLessonWithDeleteFlag,
} from '../../../../types/Tables';
import SimpleModal from '../../../uiKits/modal/basicModal/SimpleModal';
import DeleteLessonConfirm from './DeleteLessonConfirm';
import Loader from '../../../../layout/Loader';
import UploadResult from '../../upload/UploadResult';
import { CourseStateStatus, LessonWithRelationsToDelete } from '../../../../types/Courses';
import { useTranslation } from 'react-i18next';
import { getEditLessonData, getLessonRelations } from '../../../../services/api';
import { uploadInChunks } from '../../../../services/uploadInChunks';
import CommonProgress from '../../../uiKits/progress/CommonProgress';
import { getSocketSessionId } from '../../../../hooks/useSocket';
import Swal from 'sweetalert2';
import EditLessonForm from '../../../formsAndTable/forms/formControls/baseInput/EditLessonForm';
import AddLessonForm from '../../../formsAndTable/forms/formControls/baseInput/AddLessonForm';
import { AdditionalMaterial } from './CreateLesson';
import { baseLang } from '../../../../lib/utils';
import { NoDataComponent } from '../../../formsAndTable/forms/formControls/NoDataComponent';
import LessonDeleteConfirmationForm from './deleteLessonWizard';
import { DeleteLessonForm } from '../../../../types/Forms';
import { getTranslate } from '../../../../lib/translate';

const CourseDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);
  const { data, isLoading, isSuccess, status } = useGetSingleCourseQuery({ pageLang, id });

  const [openModal, setOpenModal] = useState(false);
  const [editLesson, setEditLesson] = useState<CourseLesson | null>(null);
  const [forDeleteLesson, setForDeleteLesson] = useState<LessonWithRelationsToDelete | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [takenLessonPositions, setTakenLessonPosition] = useState<number[]>([]);
  const [createNewLesson] = useCreateLessonMutation();
  const [updateLesson] = useUpdateLessonMutation();

  const [migrateLesson] = useMigrateLessonMutation();
  const [progressData, setProgressData] = useState([
    { text: 'Uploading...', class: 'primary', value: '0' },
  ]);
  useEffect(() => {
    setProgressData((prev) => {
      const newData = [...prev];
      if (currentChunk > 0 && totalChunks > 0) {
        const percentage = Math.round((currentChunk / totalChunks) * 100);
        newData[0].value = percentage.toString();
        return newData;
      }
      return prev;
    });
  }, [currentChunk, totalChunks]);

  // const getTranslate = (element: 'name' | 'description', lang: string, component: CourseData) => {
  //   if (lang === 'en') {
  //     return component[element];
  //   } else {
  //     const translation = component.translations?.find((el) => el.lang === lang);
  //     if (!translation) {
  //       return component[element];
  //     }
  //     return translation[element];
  //   }
  // };
  const handleEdit = async (lesson: CourseLesson) => {
    // open modal or navigate to edit page
    try {
      const editLessonData = await getEditLessonData(lesson.id);
      const ed = {
        ...editLessonData,
        lesson_pos: editLessonData.lesson_pos ?? undefined,
      };
      setEditLesson(ed);
      setOpenModal(true);
    } catch (error) {
      Swal.fire({
        title: 'Failed',
        text: 'Failed to load lesson!',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    }
  };

  const handleDeletelessonOpenModal = async (lesson: CourseLessonWithDeleteFlag) => {
    try {
      const res = await getLessonRelations(lesson.id);
      console.log('Lesson relations for deletion:', res);
      setForDeleteLesson(res.lesson);
      setOpenModal(true);
    } catch (error) {
      Swal.fire({
        title: 'Failed',
        text: 'Failed to load lesson relations!',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    }
  };
  const handleModalClose = () => {
    setOpenModal(false);
    setEditLesson(null);
  };
  const updateHandler = async (lesson: Partial<CourseLessonEditData>): Promise<void> => {
    const formData = new FormData();
    formData.append('id', String(editLesson?.id));
    formData.append('title', lesson.title ?? '');
    formData.append('title_uk', lesson.title_uk ?? '');
    formData.append('title_ru', lesson.title_ru ?? '');
    formData.append('description', lesson.description || '');
    formData.append('description_uk', lesson.description_uk || '');
    formData.append('description_ru', lesson.description_ru || '');
    formData.append('videoId', lesson.videoId != null ? String(lesson.videoId) : '');
    formData.append('fileName', lesson.video?.filename || '');
    formData.append('link', lesson.video?.link || '');

    if (lesson.lesson_pos != null) {
      formData.set('lesson_pos', String(lesson.lesson_pos));
    }
    const file =
      lesson.lessonCover instanceof FileList
        ? lesson.lessonCover[0]
        : lesson.lessonCover instanceof File
          ? lesson.lessonCover
          : undefined;
    if (file) {
      formData.append('lessonCover', file);
    }
    const items = Array.isArray(lesson.additionalMaterials) ? lesson.additionalMaterials : [];

    // Build JSON items + append files. We assign fileIndex for each appended file.
    const jsonItems: any[] = [];
    let fileIdx = 0;

    for (const m of items) {
      const base = {
        id: m.id ?? undefined,
        title: m.title ?? '',
        title_uk: m.title_uk ?? '',
        title_ru: m.title_ru ?? '',
        kind: m.kind ?? 'link',
        // position: m.position ?? 0,
        // For link-kind we keep URL, for file-kind we’ll set url only if no new file is provided
        url: m.kind === 'link' ? (m.url ?? '') : (m.url ?? undefined),
      };

      if (m.kind === 'file') {
        // New file selected for this material?
        const selectedFile =
          (m as any).file instanceof FileList
            ? (m as any).file[0]
            : (m as any).file instanceof File
              ? (m as any).file
              : undefined;

        if (selectedFile) {
          // Append file into `additionalFiles[]` and reference it via fileIndex
          formData.append('additionalFiles', selectedFile);
          (base as any).fileIndex = fileIdx; // 👈 tell server which file belongs here
          fileIdx += 1;
          // Optional: you can omit base.url so backend replaces it with the new file URL
          delete (base as any).url;
        } else {
          // No new file selected:
          // - if it's an existing row (id present), keep old url or omit; backend will keep previous file
          // - if it's a NEW row (no id) and no file provided → backend will reject (as intended)
          if (!base.id && !base.url) {
            // Creating a brand-new file item without a file will fail on backend
            // You can pre-validate on FE if you want.
          }
        }
      }

      jsonItems.push(base);
    }
    formData.set('additionalMaterials', JSON.stringify(jsonItems));
    try {
      if (!editLesson || !editLesson.id) {
        return console.log('No lesson id');
      }
      const body = {
        formData,
        id: editLesson.id,
      };
      const res = await updateLesson(body).unwrap();
    } catch (err) {
      console.error('Update error:', err);
    } finally {
      handleModalClose();
    }
  };

  const deleteHandler = async (deleteData: DeleteLessonForm) => {
    console.log('Deleting lesson with data:', deleteData);
    try {
      const res = await migrateLesson(deleteData).unwrap();
      if (res.success) {
        Swal.fire({
          title: 'Success',
          text: 'Lesson deleted successfully!',
          icon: 'success',
          confirmButtonColor: Primary,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete lesson.',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    } finally {
      handleModalClose();
      setForDeleteLesson(null);
    }
  };

  const handleCreateNewLesson = async (
    courseId: number,
    lessonName: {
      en: {
        name: string;
        id: number;
      };
      uk: {
        name: string;
        id: number;
      };
      ru: {
        name: string;
        id: number;
      };
    },
    lessonDescription: {
      en: {
        text: string;
        id: number;
      };
      uk: {
        text: string;
        id: number;
      };
      ru: {
        text: string;
        id: number;
      };
    },
    file: File,
    // fileLink: string,
    token: string,
    category: { name: string; id: number },
    image: File | null,
    courseState: CourseStateStatus,
    lessonPosition: number,
    additionals?: AdditionalMaterial[],
    videoTitle?: {
      en: string;
      uk: string;
      ru: string;
    },
  ): Promise<{ uploaded: boolean }> => {
    const formData = new FormData();
    setOpenModal(false);
    setShowProgress(true);
    const uploadResult = await uploadInChunks(
      file,
      20 * 1024 * 1024,
      (current: number, total: number) => {
        setCurrentChunk(current);
        setTotalChunks(total);
      },
    ); // ⬅ wait for server to return it
    setShowProgress(false);
    setProgressData([{ text: 'Uploading...', class: 'primary', value: '0' }]);
    setCurrentChunk(0);
    setTotalChunks(0);
    if (!uploadResult.filePath) {
      console.error('❌ No filePath returned from upload');
      return { uploaded: false };
    }
    const sessionId = getSocketSessionId();
    formData.append('lessonName', lessonName.en.name || '');
    formData.append('lessonName_uk', lessonName.uk.name || '');
    formData.append('lessonName_ru', lessonName.ru.name || '');
    formData.append('description', lessonDescription.en.text || '');
    formData.append('description_uk', lessonDescription.uk.text || '');
    formData.append('description_ru', lessonDescription.ru.text || '');
    // formData.append('fileLink', fileLink);
    formData.append('courseId', String(courseId));
    formData.append('categoryName', category.name);
    formData.append('categoryId', String(category.id));
    formData.append('courseStateStatus', courseState);
    formData.append('videoFilePath', uploadResult.filePath);
    formData.append('sessionId', sessionId);
    if (videoTitle) {
      formData.append('videoTitle_en', videoTitle.en || '');
      formData.append('videoTitle_uk', videoTitle.uk || '');
      formData.append('videoTitle_ru', videoTitle.ru || '');
    }
    // Build additionalMaterials + append files
    const items = additionals || []; // your UI array
    const jsonItems: any[] = [];
    let fileIdx = 0;

    for (const it of items) {
      const base: any = {
        id: it.id ?? undefined,
        title: it.title ?? '',
        title_uk: it.title_uk ?? '',
        title_ru: it.title_ru ?? '',
        kind: it.kind ?? 'link',
        // position: it?.position ?? 0,
      };

      if (base.kind === 'file') {
        const f =
          it.file instanceof FileList ? it.file[0] : it.file instanceof File ? it.file : undefined;

        if (f) {
          formData.append('additionalFiles', f);
          base.fileIndex = fileIdx++; // 👈 match JSON item to the appended file
          // omit url; backend will set `/uploads/additionals/lesson_{id}/<filename>`
        } else if (it.url) {
          base.url = it.url; // editing case (not typical for create)
        } else {
          // creating a new file item must have a file; you can pre-validate here
        }
      } else {
        // link item
        base.url = it.url ?? '';
      }

      jsonItems.push(base);
    }

    formData.set('additionalMaterials', JSON.stringify(jsonItems));

    if (lessonPosition != null) {
      formData.set('lesson_pos', String(lessonPosition));
    }
    if (image) {
      formData.append('lessonCover', image);
    }

    try {
      // Array.from(formData.entries()).forEach(([key, value]) => {
      //   console.log(`${key}:`, value);
      // });
      const res = await createNewLesson(formData).unwrap();
      return { uploaded: true };
    } catch (err) {
      console.error('Upload error:', err);
      return { uploaded: false };
    }
  };
  const selectCategoryTranslation = (cat: CourseCategory) => {
    if (pageLang === 'en') {
      return cat.name;
    } else {
      if (!cat.translations || cat.translations.length === 0) {
        return cat.name;
      }
      const categoryTranslation = cat.translations.find(
        (el: { id: number; lang: string; name: string }) => el.lang === pageLang,
      );
      return categoryTranslation?.name ?? cat.name;
    }
  };
  const pageParents = [
    {
      name: t('courses.coursesList.coursesListPageTitle'),
      link: '/courses/courses-list',
    },
  ];
  if (status === 'pending') return <Loader />;
  if (status === 'rejected') return <div className="text-danger">Failed to load course</div>;
  if (status === 'fulfilled' && data?.course && data.course.categories?.length)
    return (
      <>
        <Breadcrumbs
          mainTitle={getTranslate('name', pageLang, data.course) ?? data.course.name}
          // parent={t('courses.coursesList.coursesListPageTitle')}
          parentsArray={pageParents}
        />
        <Container fluid>
          <Row>
            <Col sm="12">
              <Card>
                <CommonCardHeader
                  title={`${getTranslate('name', pageLang, data.course) ?? data.course.name}: ${t('courses.coursesList.courseDetailsCardTitle')}`}
                  tag
                />
                <CardBody>
                  <p>
                    {getTranslate('description', pageLang, data.course) ??
                      t('courses.coursesList.noDescription')}
                  </p>
                  <Button
                    color="primary"
                    outline
                    onClick={() => setOpenModal(true)}
                    className="mb-4"
                  >
                    {t('courses.lessons.addNewLessonBtn')}
                  </Button>
                  {data &&
                    data.course.categories.map((category: any) => {
                      return (
                        <AjaxSourcedData
                          key={category.id}
                          data={category.lessons}
                          columns={getLessonsListTableColumns(
                            handleEdit,
                            handleDeletelessonOpenModal,
                            id ?? '',
                            t,
                          )}
                          title={selectCategoryTranslation(category)}
                          noDataComponent={<NoDataComponent />}
                        />
                      );
                    })}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <SimpleModal
            isOpen={openModal}
            close={() => handleModalClose()}
            size={editLesson ? 'xl' : 'lg'}
            centered
          >
            {editLesson && (
              <EditLessonForm
                name={data.course.name}
                editLesson={editLesson}
                categories={data.course.categories}
                courseId={data.course.id}
                courseStateStatus={data.course.courseStateStatus}
                updateHandler={updateHandler}
                close={() => handleModalClose()}
                takenPositions={takenLessonPositions}
              />
            )}

            {forDeleteLesson && (
              // <DeleteLessonConfirm
              //   deleteLesson={forDeleteLesson}
              //   deleteHandler={deleteHandler}
              //   close={() => handleModalClose()}
              // />
              <LessonDeleteConfirmationForm
                deleteLesson={forDeleteLesson}
                handleDelete={deleteHandler}
                close={() => handleModalClose()}
              />
            )}
            {!editLesson && !forDeleteLesson && (
              <AddLessonForm
                name={data.course.name}
                courseId={data.course.id}
                categories={data.course.categories}
                courseStateStatus={data.course.courseStateStatus}
                createHandler={handleCreateNewLesson}
                close={() => handleModalClose()}
                setShowConfirm={setShowConfirm}
                setUploadSuccess={setUploadSuccess}
                takenPositions={takenLessonPositions}
              />
            )}
          </SimpleModal>
          <SimpleModal isOpen={showProgress} close={() => setShowProgress(false)}>
            <CommonProgress
              title={'Uploading video'}
              data={progressData}
              striped
              animated
              tag
              progressClass={'mb-4 text-center'}
              value
            />
          </SimpleModal>
          <SimpleModal isOpen={showConfirm} close={() => setShowConfirm(false)}>
            <UploadResult
              uploadSuccess={uploadSuccess}
              setShowConfirm={setShowConfirm}
              close={() => setShowConfirm(false)}
            />
          </SimpleModal>
        </Container>
      </>
    );
  return null;
};

export default CourseDetailsPage;
